import { cn } from '@/lib/utils'
import { distinctIcons, iconKey, toolIcons } from '@/lib/toolIcons'
import { ToolGlyph } from '@/components/ToolLogo'

function WorkflowArt() {
  const nodes = [
    { x: 14, y: 22, label: 'WhatsApp', tone: '#4ade80' },
    { x: 118, y: 58, label: 'AI agent', tone: '#00e5ff' },
    { x: 222, y: 22, label: 'Sheets', tone: '#fcd34d' },
    { x: 222, y: 94, label: 'Reply', tone: '#a5b4fc' },
  ]
  return (
    <svg viewBox="0 0 300 132" className="h-full w-full" fill="none">
      <path
        className="animate-dash"
        d="M 82 36 C 100 36, 100 72, 118 72"
        stroke="rgba(0,229,255,0.55)"
        strokeWidth="1.5"
        strokeDasharray="4 6"
      />
      <path
        className="animate-dash"
        d="M 186 72 C 204 72, 204 36, 222 36"
        stroke="rgba(0,229,255,0.55)"
        strokeWidth="1.5"
        strokeDasharray="4 6"
      />
      <path
        className="animate-dash"
        d="M 186 72 C 204 72, 204 108, 222 108"
        stroke="rgba(0,229,255,0.55)"
        strokeWidth="1.5"
        strokeDasharray="4 6"
      />
      {nodes.map((node) => (
        <g key={node.label}>
          <rect
            x={node.x}
            y={node.y}
            width="68"
            height="28"
            rx="8"
            fill="#101317"
            stroke="rgba(255,255,255,0.12)"
          />
          <circle cx={node.x + 12} cy={node.y + 14} r="4" fill={node.tone} />
          <text
            x={node.x + 22}
            y={node.y + 18}
            fill="#cdd3da"
            fontSize="10"
            fontFamily="Geist Mono, monospace"
          >
            {node.label}
          </text>
        </g>
      ))}
    </svg>
  )
}

function BrowserArt() {
  return (
    <div className="h-full w-full rounded-xl border border-white/10 bg-[#0b0d10] shadow-[0_20px_40px_-20px_rgba(0,0,0,0.8)]">
      <div className="flex items-center gap-1.5 border-b border-white/[0.06] px-3 py-2">
        <span className="size-2 rounded-full bg-[#ff5f57]/80" />
        <span className="size-2 rounded-full bg-[#febc2e]/80" />
        <span className="size-2 rounded-full bg-[#28c840]/80" />
        <span className="ml-3 h-3.5 w-28 rounded-full bg-white/[0.06] px-2 font-code text-[8px] leading-[14px] text-[var(--surface-muted)]">
          your-app.dev
        </span>
      </div>
      <div className="grid grid-cols-[52px_1fr] gap-3 p-3">
        <div className="space-y-1.5">
          <span className="block h-1.5 w-10 rounded bg-[var(--color-brand-accent)]/60" />
          <span className="block h-1.5 w-8 rounded bg-white/10" />
          <span className="block h-1.5 w-9 rounded bg-white/10" />
          <span className="block h-1.5 w-7 rounded bg-white/10" />
        </div>
        <div className="space-y-2">
          <div className="flex items-end gap-1.5">
            {[38, 62, 46, 80, 58, 92, 70].map((height) => (
              <span
                key={height}
                className="w-3 rounded-sm bg-[linear-gradient(180deg,rgba(0,229,255,0.75),rgba(0,229,255,0.15))]"
                style={{ height: `${height * 0.42}px` }}
              />
            ))}
          </div>
          <p className="font-code text-[9px] leading-4 text-[var(--surface-muted)]">
            <span className="text-[#c4b5fd]">export</span>{' '}
            <span className="text-[var(--color-brand-accent-readable)]">async function</span>{' '}
            <span className="text-[var(--text-strong)]">checkout</span>()
            <span className="animate-caret ml-0.5 inline-block h-2.5 w-[5px] translate-y-0.5 bg-[var(--color-brand-accent)]" />
          </p>
        </div>
      </div>
    </div>
  )
}

function PhoneArt() {
  return (
    <div className="mx-auto h-full w-[118px] rounded-[20px] border border-white/12 bg-[#0b0d10] p-2 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.9)]">
      <div className="mx-auto mb-2 h-1.5 w-10 rounded-full bg-white/10" />
      <div className="space-y-1.5 px-1">
        <p className="w-fit max-w-[82px] rounded-lg rounded-bl-sm bg-white/[0.07] px-2 py-1 text-[8px] leading-3 text-[var(--surface-fg)]">
          Plan my study week
        </p>
        <p className="ml-auto w-fit max-w-[86px] rounded-lg rounded-br-sm bg-[var(--color-brand-accent)]/20 px-2 py-1 text-[8px] leading-3 text-[var(--color-brand-accent-readable)]">
          3 sessions added ✓
        </p>
        <p className="w-fit max-w-[82px] rounded-lg rounded-bl-sm bg-white/[0.07] px-2 py-1 text-[8px] leading-3 text-[var(--surface-fg)]">
          Remind me at 8
        </p>
      </div>
    </div>
  )
}

function KeyboardArt() {
  const rows = ['QWERTY', 'ASDFG', 'ZXCV']
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5">
      {rows.map((row) => (
        <div key={row} className="flex gap-1.5">
          {row.split('').map((key) => (
            <span
              key={key}
              className="flex size-6 items-center justify-center rounded-md border border-white/10 bg-[#101317] font-code text-[9px] text-[var(--surface-fg)] shadow-[0_2px_0_rgba(255,255,255,0.05)]"
            >
              {key}
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

const CLUSTER_SLOTS = [
  { left: '50%', top: '50%', size: 'size-14', glyph: 26 },
  { left: '27%', top: '38%', size: 'size-11', glyph: 20 },
  { left: '73%', top: '38%', size: 'size-11', glyph: 20 },
  { left: '9%', top: '64%', size: 'size-9', glyph: 16 },
  { left: '91%', top: '64%', size: 'size-9', glyph: 16 },
]

function clusterIcons(tools: string[]) {
  const icons = distinctIcons(tools.flatMap(toolIcons))
  return [
    ...icons.filter((icon) => icon.kind === 'brand'),
    ...icons.filter((icon) => icon.kind === 'generic'),
  ].slice(0, CLUSTER_SLOTS.length)
}

function LogoClusterArt({ tools }: { tools: string[] }) {
  const icons = clusterIcons(tools)
  return (
    <div className="relative h-full">
      <span className="absolute left-1/2 top-1/2 size-28 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/10" />
      <span className="absolute left-1/2 top-1/2 size-48 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.05]" />
      <span className="absolute left-1/2 top-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(0,229,255,0.18),transparent_70%)]" />
      {icons.map((icon, index) => {
        const slot = CLUSTER_SLOTS[index]
        return (
          <span
            key={iconKey(icon)}
            className={cn(
              'absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/10 bg-[#101317] shadow-[0_14px_30px_-14px_rgba(0,0,0,0.9)]',
              slot.size,
            )}
            style={{ left: slot.left, top: slot.top }}
          >
            <ToolGlyph icon={icon} size={slot.glyph} />
          </span>
        )
      })}
    </div>
  )
}

export function ProgramArt({
  category,
  tools = [],
  className,
}: {
  category: string
  tools?: string[]
  className?: string
}) {
  const art =
    category === 'AI & Automation' ? (
      <WorkflowArt />
    ) : category === 'Web Development' ? (
      <BrowserArt />
    ) : category === 'Mobile' ? (
      <PhoneArt />
    ) : category === 'Foundation' ? (
      <KeyboardArt />
    ) : (
      <LogoClusterArt tools={tools} />
    )

  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative h-36 overflow-hidden rounded-2xl border border-white/[0.06] bg-[radial-gradient(ellipse_at_top,rgba(0,229,255,0.1),transparent_65%),linear-gradient(180deg,#0f1216,#0a0c0f)] px-4 py-3',
        className,
      )}
    >
      <div className="absolute inset-0 bg-dots opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <div className="relative h-full">{art}</div>
    </div>
  )
}
